/*
 * Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import { LOAD_STATES, PagedRequestHandler } from "@/utils/PagedRequestHandler"
import { castValueToType } from "@/utils/TypeCastHelpers"
import { EventBus } from "~/common/src/event-bus"
import { LICENSING_PACKAGES_UPDATED, LICENSING_ASSIGNMENTS_UPDATED } from "@/config/events"
import Vue from "vue"
import api from "@/config/api"

/* global log */

const packageRequestHandler = Vue.observable(new PagedRequestHandler(api.LICENSES_PACKAGES))
const featureRequestHandler = Vue.observable(new PagedRequestHandler(api.LICENSES_FEATURES))

const ASSIGNMENT_PAGE_SIZE = 5
const tenantAssignmentHandlers = {}

/**
 * Helper method that takes a set of properties and casts all held values
 * to that defined by the given feature list.
 *
 * @param {Object} properties - containing values to be casted
 * @param {Array} features - defining the value type to cast to
 * @returns {Object} given object after mutating values
 */
const typeCastAllMatchingProperties = function (properties, features) {
  for (const feature of features) {
    if (!properties[feature.name]) continue

    for (const qualifier in properties[feature.name]) {
      properties[feature.name][qualifier] = castValueToType(properties[feature.name][qualifier], feature.valueType)
    }
  }

  return properties
}

export default {
  namespaced: true,

  state: {
    activeTenantId: "",

    /**
     * Intended structure
     * {
     *    tenantId: {
     *                  future: [], // Future assignment group including current license
     *                  past: []
     *              }
     * }
     */
    assignments: {},

    packages: {
      // TODO: this store logic should be refactored to mirror tenant store; see:
      // https://jira.teambits.cloud/browse/DEV-2525?focusedCommentId=48174&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-48174
      baseKeyMap: {},
      baseList: [],
      assignedKeyMap: {},
      assignedList: [],
    },

    features: {
      list: [],
    },
  },

  mutations: {
    /**
     * Stores the provided tenant id as the active tenant id
     *
     * @param {*} activeTenant - id of current user
     */
    setActiveTenantId(state, activeTenant) {
      state.activeTenantId = activeTenant
    },

    /**
     * Stores provided license packages to state. Purges old state data if
     * flag is set.
     *
     * @param {Object} {
     *              packages - list of packages to store
     *              purge - flag indicating if current data should be removed
     *            }
     */
    setLicensePackages(state, { packages, purge }) {
      if (purge) {
        Vue.set(state.packages, "baseKeyMap", {})
        Vue.set(state.packages, "baseList", [])
      }

      // Store the list and for each maintain a pointer to its location in the list
      packages.forEach((p) => {
        let newKeyIdx = state.packages.baseList.push(p) - 1

        Vue.set(state.packages.baseKeyMap, p.id, newKeyIdx)
      })
    },

    /**
     * Stores provided license package to state or updates an existing package
     * if one exists
     *
     * @param {Object} {
     *              license - to store
     *            }
     */
    setLicensePackage(state, { license }) {
      let currentPackageIdx = state.packages.baseKeyMap[license.id]
      // Check if package already exists in our list
      if (currentPackageIdx !== undefined) {
        Vue.set(state.packages.baseList, currentPackageIdx, license)
      } else {
        let newLength = state.packages.baseList.push(license)

        Vue.set(state.packages.baseKeyMap, license.id, newLength - 1)
      }
    },

    /**
     * Removes the indicated license from state and updates key information
     *
     * @param {Object} {
     *              licenseId - for the target license
     *            }
     */
    removeLicensePackage(state, { licenseId }) {
      let licenseIdx = state.packages.baseKeyMap[licenseId]

      Vue.delete(state.packages.baseList, licenseIdx)
      state.packages.baseList.forEach((p, idx) => {
        Vue.set(state.packages.baseKeyMap, p.id, idx)
      })
    },

    // ------- License features

    /**
     * Stores provided license features to state. Purges old state data if
     * flag is set.
     *
     * @param {Object} {
     *              features - list of features to store
     *              purge - flag indicating if current data should be removed
     *            }
     */
    setLicenseFeatures(state, { features, purge }) {
      if (purge) {
        Vue.set(state.features, "list", [])
      }

      Vue.set(state.features, "list", state.features.list.concat(features))
    },

    // ------- License assignments

    /**
     * Stores the current license assignments for effective user
     *
     * @param {Object} {
     *              tenantId - associated to license assignments
     *              assignments - license assignments associated to user
     *            }
     */
    setLicenseAssignments(state, { tenantId, assignments }) {
      // Override everything, this will need to be tweaked when advanced paging
      // is desired
      Vue.set(state.assignments, tenantId, {})

      Vue.set(state.assignments[tenantId], "future", assignments.future)
      Vue.set(state.assignments[tenantId], "past", assignments.past)
    },

    /**
     * Stores packages assigned to a tenant to the assigned list
     *
     * @param {Object} {
     *              packages - associated to license assignments
     *            }
     */
    setLicenseAssignmentPackages(state, { packages }) {
      // Store the list and for each maintain a pointer to its location in the list
      packages.forEach((p) => {
        if (state.packages.baseKeyMap[p.id]) return

        let newKeyIdx = state.packages.assignedList.push(p) - 1

        Vue.set(state.packages.assignedKeyMap, p.id, newKeyIdx)
      })
    },
  },

  actions: {
    /**
     * Perform the request for license packages from server.
     *
     * @param {Object} {
     *              page - to retrieve
     *              search - string to filter by
     *              filter - list of assignment states to filter by
     *            }
     */
    loadLicensePackages({ commit, getters }, { page, search, filter }) {
      if (packageRequestHandler.LoadStatus === LOAD_STATES.LOADING) return

      if (page !== undefined) packageRequestHandler.setCurrentPage(page)
      if (search !== undefined) packageRequestHandler.setSearchString(search)
      if (filter !== undefined) packageRequestHandler.setFilter(filter)
      packageRequestHandler
        .retrieveNextPage()
        .then((result) => {
          for (const license of result) {
            if (!license.licenseFeatures) continue
            // If the list of features has not been loaded prior to this call, this will do nothing
            license.licenseFeatures = typeCastAllMatchingProperties(license.licenseFeatures, getters["getFeatureList"])
          }
          commit("setLicensePackages", {
            packages: result,
            purge: page === 0,
          })
        })
        .catch((error) => log.warn(error))
    },

    /**
     * Perform request to create a new license package on server and commits
     * returned package on success.
     *
     * @param {Object} {
     *              license - to create
     *            }
     */
    createLicensePackage({ commit, dispatch }, { license }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.LICENSES_PACKAGES(), license).then(
          (resp) => {
            commit("setLicensePackage", {
              license: resp.body,
            })

            // Reload package set to align with server
            dispatch("loadLicensePackages", { page: 0 })
            resolve(resp.body)
          },
          (error) => {
            reject(error)
          }
        )
      })
    },

    /**
     * Perform request to update an existing license package on the server.
     *
     * @param {Object} {
     *              license - to update
     *            }
     */
    updateLicensePackage({ commit, dispatch }, { license }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.LICENSES_PACKAGES(license.id), license).then(
          (resp) => {
            commit("setLicensePackage", {
              license: resp.body,
            })

            dispatch("loadLicensePackages", { page: 0 })
            EventBus.safeTrigger(LICENSING_PACKAGES_UPDATED)
            resolve(resp.body)
          },
          (error) => {
            reject(error)
          }
        )
      })
    },

    /**
     * Perform request to delete package from server
     *
     * @param {Object} {
     *              licenseId - of package to delete
     *            }
     */
    deleteLicensePackage({ commit, dispatch }, { licenseId }) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.LICENSES_PACKAGES(licenseId)).then(
          () => {
            commit("removeLicensePackage", {
              licenseId,
            })

            // Reload package set to align with server
            dispatch("loadLicensePackages", { page: 0 })
            EventBus.safeTrigger(LICENSING_PACKAGES_UPDATED)
            resolve(licenseId)
          },
          (error) => {
            log.error("Failed to perform delete request : ", error)

            reject(error)
          }
        )
      })
    },

    // ------- License features

    /**
     * Perform the request for license features from server.
     *
     * @param {Object} {
     *              page - to retrieve
     *            }
     */
    loadLicenseFeatures({ commit }, { page }) {
      if (featureRequestHandler.LoadStatus === LOAD_STATES.LOADING) return

      if (page !== undefined) featureRequestHandler.setCurrentPage(page)
      featureRequestHandler
        .retrieveNextPage()
        .then((result) => {
          commit("setLicenseFeatures", {
            features: result,
            purge: page === 0,
          })
        })
        .catch((error) => log.warn(error))
    },

    // ------- License assignments

    /**
     * Perform the request for the license assignments associated to the given tenant.
     *
     * @param {Object} {
     *              tenantId - of tenant to associate assignments by
     *              page - to retrieve
     *              filter - to filter page set by
     *            }
     */
    loadTenantLicenseAssignments({ commit, getters }, { tenantId, page, filter }) {
      new Promise((resolve, reject) => {
        let handler = null

        if (!tenantId) {
          log.error("A tenant id must be provided in order to load a tenant's set of license assignments")
          return
        }
        // First check if the tenant already has a handler built for it
        if (!tenantAssignmentHandlers[tenantId]) {
          // Build a handler and associate it to the tenantId
          tenantAssignmentHandlers[tenantId] = Vue.observable(
            new PagedRequestHandler(api.TENANT_LICENSE_ASSIGNMENT, ASSIGNMENT_PAGE_SIZE, [tenantId])
          )
        }
        // Get associated handler; at this point handler should never be null
        handler = tenantAssignmentHandlers[tenantId]

        if (handler.LoadStatus === LOAD_STATES.LOADING) resolve()

        // Now set the page information for the handler
        if (page !== undefined) handler.setCurrentPage(page)
        if (filter !== undefined) handler.setFilter(filter)
        handler
          .retrieveNextPage()
          .then(async (result) => {
            const currentDate = Date.now()
            let assignments = {
              future: [],
              past: [],
            }

            for (const assigned of result) {
              let assignedEndDate = assigned.endDate ? new Date(assigned.endDate).getTime() : null

              if (!assignedEndDate || assignedEndDate >= currentDate) {
                // Push a future license into the stack (not sure how many will be permitted)
                assignments.future.push(assigned)
              } else {
                // For now assume that all licenses that aren't currently active are past
                assignments.past.push(assigned)
              }

              // Check if the package has already been loaded by some other request
              if (!getters["getPackageById"](assigned.licensePackageId)) {
                // For each, load the package information associated in prepartion for use elsewhere
                try {
                  let resp = await Vue.http.get(api.LICENSES_PACKAGES(assigned.licensePackageId))
                  commit("setLicenseAssignmentPackages", {
                    packages: [resp.body],
                  })
                } catch (error) {
                  log.error("Failed to retrieve license package associated to assignment : ", error)
                }
              }
            }

            commit("setLicenseAssignments", {
              tenantId,
              assignments,
            })

            resolve()
          }, reject)
          .catch((error) => log.warn(error))
      })
    },

    /**
     * Perform the request to create a package assignment on the given tenant.
     *
     * @param {Object} {
     *              tenantId - of tenant to associate assignment
     *              assignment - to associate
     *            }
     */
    assignLicenseToTenant({ dispatch }, { tenantId, assignment }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.TENANT_LICENSE_ASSIGNMENT(tenantId), assignment).then(
          (resp) => {
            dispatch("loadTenantLicenseAssignments", { tenantId, page: 0 })
            EventBus.safeTrigger(LICENSING_ASSIGNMENTS_UPDATED)
            resolve(resp.body)
          },
          (error) => {
            log.error("Failed to create license assignment for tenant : ", tenantId, " : ", error)

            reject(error)
          }
        )
      })
    },

    /**
     * Perform the request to update an existing assignment associated to the tenant
     * specified by the given id.
     *
     * @param {Object} {
     *              tenantId - of tenant to associate assignments by
     *              assignment - with updates to store
     *            }
     */
    updateLicenseAssignmentForTenant({ dispatch }, { tenantId, assignment }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.TENANT_LICENSE_ASSIGNMENT(tenantId, assignment.id), assignment).then(
          (resp) => {
            dispatch("loadTenantLicenseAssignments", { tenantId, page: 0 })
            EventBus.safeTrigger(LICENSING_ASSIGNMENTS_UPDATED)
            resolve(resp.body)
          },
          (error) => {
            log.error("Failed to update license assignment for tenant : ", tenantId, " : ", error)

            reject(error)
          }
        )
      })
    },

    /**
     * Perform the request to delete the assignment specified by the given assignment id
     * for the tenant specified by the given tenant id.
     *
     * @param {Object} {
     *              tenantId - of tenant to associate assignments by
     *              assignmentId - of assignment to delete
     *            }
     */
    deleteLicenseAssignmentOfTenant({ dispatch }, { tenantId, assignmentId }) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.TENANT_LICENSE_ASSIGNMENT(tenantId, assignmentId)).then(
          (resp) => {
            dispatch("loadTenantLicenseAssignments", { tenantId, page: 0 })
            resolve(resp.body)
          },
          (error) => {
            log.error("Failed to delete license assignment for tenant : ", tenantId, " : ", error)

            reject(error)
          }
        )
      })
    },
  },

  getters: {
    /**
     * Returns current load state of activities on store. This includes initial loads
     * as well as loading of next pages.
     *
     * @param {*} state
     * @returns String
     */
    getPackageLoadState: () => packageRequestHandler.LoadStatus,

    /**
     * Returns the list of license packages currently held in state
     * @param {*} state
     * @returns Array
     */
    getPackageList: (state) => state.packages.baseList,

    /**
     * Returns the license package with the given id, if existing. If not exists, return null
     *
     * @param {*} state
     * @returns Object | null
     */
    getPackageById: (state) => (id) => {
      let licensePackage = null

      if (state.packages.baseKeyMap[id] !== undefined)
        licensePackage = state.packages.baseList[state.packages.baseKeyMap[id]]
      else if (state.packages.assignedKeyMap[id] !== undefined)
        licensePackage = state.packages.assignedList[state.packages.assignedKeyMap[id]]

      return licensePackage
    },

    /**
     * Returns the default license package
     */
    getDefaultPackage: (state) => {
      const defaultPackage = state.packages.baseList.find((licensePackage) => licensePackage.default === true)
      if (defaultPackage) {
        return defaultPackage
      }
      if (state.packages.baseList.length > 0) {
        return state.packages.baseList[0]
      }
      // no license packages at all or not yet loaded
      return null
    },

    /**
     * Returns a boolean indicating if another page of license packages can be retrieved
     *
     * @returns Boolean
     */
    canRetrieveMorePackages: () => packageRequestHandler.CurrentPage < packageRequestHandler.TotalPages - 1,

    /**
     * Returns whether the effective license has been loaded to state.
     * @returns Boolean
     */
    isEffectiveLicenseLoaded: (state) => state.effectiveLicense.loadStatus !== LOAD_STATES.UNLOADED,

    /**
     * Returns current load state of activities on store. This includes initial loads
     * as well as loading of next pages.
     *
     * @param {*} state
     * @returns String
     */
    getLicenseAssignmentsLoadState:
      (state) =>
      (tenantId = state.activeTenantId) =>
        tenantAssignmentHandlers[tenantId]?.LoadStatus,

    /**
     * Returns the license associated to a given tenantId. If no tenantId is given, it
     * is assumed the license assignments for the default/current user is desired.
     *
     * @param {*} state
     * @returns Object
     */
    getLicenseAssignments:
      (state) =>
      (tenantId = null) => {
        if (tenantId === null) tenantId = state.activeTenantId

        if (!state.assignments[tenantId]) return null

        return {
          future: state.assignments[tenantId].future,
          past: state.assignments[tenantId].past,
        }
      },

    /**
     * Returns current load state of activities on store. This includes initial loads
     * as well as loading of next pages.
     *
     * @param {*} state
     * @returns String
     */
    getFeatureLoadState: () => featureRequestHandler.LoadStatus,

    /**
     * Returns the list of license features currently held in state
     *
     * @param {*} state
     * @returns Array
     */
    getFeatureList: (state) => state.features.list,
  },
}
